// src/belediye/components/mesajlasma/YerAdimi.js
// FAZ-83/Y2 — MAHALLE, KONUM ve ADRES TEK ADIMDA (üç sekme).
//
// Kullanıcı kararı (2026-08-01): "mahalle konum için de Y2 — üç sekme, araç
// tam yükseklik seçeneğini seçiyorum." İstenen sıra: üstte mahalle, ortada
// konum, altta adres — sekme çipleri de bu sırada.
//
// FAZ-83/2 (aynı gün, ikinci tur): "yer seçeneğinde de ANA MESAJ KUTUSUNU
// kullanalım, gidip yeniden bir alan oluşturma. Mahalle arama kısmı ana mesaj
// kutusunda olsun ve seçilenler yine buraya yazılsın; aynı mantıkta konum,
// aynı mantıkta açık adres."
// Bu yüzden alt satır ÜÇ sekmede de aynı yerde durur ve yalnız İÇİ değişir:
//   • Mahalle → seçilen çipler + arama kutusu
//   • Konum   → işaretin özeti
//   • Adres   → adres yazma kutusu
// Atla ve Gönder ADIMIN tamamına aittir; sekmeye göre değişmez.
//
// Üçü AYRI bilgidir, biri diğerinin yerine geçmez (FAZ-55/9'dan devralınan
// ayrım korunuyor):
//   • mahalle → idari bölge (raporlanabilir, süzülebilir; ÇOKLU)
//   • konum   → haritadaki nokta (ekip tam oraya gidecek)
//   • adres   → insanın tarif ettiği yer ("çarşı girişi, fırının yanı")
//
// SEKME DEĞİŞİNCE İÇERİK SÖKÜLÜR (yalnız etkin sekme monte edilir). Gerekçe:
// harita ayrı bir paket ve gizlenmiş bir Leaflet haritası 0 boyutla açılıp
// bozuluyor. Sökülen sekmenin VERİSİ burada durduğu için hiçbir şey
// kaybolmaz — geri gelindiğinde `initial` ile aynı yerinden devam eder.
//
// Sözleşme: onDone({ neighborhood_ids?, neighborhood_names?, address?,
//                    latitude?, longitude? }) — yalnız DOLU olanlar gider.
import React, { useEffect, useRef, useState } from "react";
import { CheckIcon } from "@heroicons/react/24/outline";

import KonumSecici from "./KonumSecici";
import MahallePicker from "./MahallePicker";
import SihirbazAltSatir from "./SihirbazAltSatir";

// Açık adres — ADIMIN kendi kutusu değil, ana mesaj kutusunun içeriği.
// FAZ-83/3: yer tutucu KISA ("Açık adres…"). Uzun metin kutuda sarıyor ve
// otomatik büyüme onu içerik sanıp kutuyu boşken bile aşağı çekiyordu;
// kullanıcı: "bu yazı gerçek bir yazıymış gibi mesaj kutusunu aşağı
// sürüklüyor… tasarımı bozmasın."
function AdresIcerik({ deger, onDegis }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!deger) {
      el.style.height = "";
      return;
    }
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 108)}px`;
  }, [deger]);
  return (
    <textarea
      ref={ref}
      rows={1}
      value={deger}
      maxLength={255}
      placeholder="Açık adres…"
      aria-label="Açık adres"
      autoFocus
      onChange={(e) => onDegis(e.target.value)}
    />
  );
}

export default function YerAdimi({ acc = {}, onDone, onSkip, pasif = false }) {
  const [sekme, setSekme] = useState("mahalle");
  const [mahalleler, setMahalleler] = useState(() =>
    (acc.neighborhood_ids || []).map((id, i) => ({
      id,
      name: (acc.neighborhood_names || [])[i] || "",
    }))
  );
  const [nokta, setNokta] = useState(() =>
    acc.latitude != null && acc.longitude != null
      ? { lat: Number(acc.latitude), lng: Number(acc.longitude) }
      : null
  );
  const [adres, setAdres] = useState(acc.address || "");

  const doluMu = {
    mahalle: mahalleler.length > 0,
    konum: !!nokta,
    adres: adres.trim().length > 0,
  };
  const hicbiriYok = !doluMu.mahalle && !doluMu.konum && !doluMu.adres;
  // FAZ-83/3 (kullanıcı): "bunu yer kısmında da yapalım aynı mantıkta — biri
  // yazılınca diğeri de [hatırlatsın]." Bir sekme doldurulduysa, HENÜZ BOŞ
  // olan öteki sekmelerden dalga çıkar. Üçü de isteğe
  // bağlı olduğu için bu bir uyarı değil, "burası da var" demek.
  const birseyDolu = doluMu.mahalle || doluMu.konum || doluMu.adres;
  const parlaMi = (k) => birseyDolu && !doluMu[k] && sekme !== k;

  const SEKMELER = [
    { key: "mahalle", ad: "Mahalle" },
    { key: "konum", ad: "Konum" },
    { key: "adres", ad: "Adres" },
  ];

  const gonder = () => {
    if (pasif || hicbiriYok) return;
    const p = {};
    if (mahalleler.length) {
      p.neighborhood_ids = mahalleler.map((m) => m.id);
      p.neighborhood_names = mahalleler.map((m) => m.name);
    }
    if (nokta) {
      p.latitude = nokta.lat;
      p.longitude = nokta.lng;
    }
    if (adres.trim()) p.address = adres.trim();
    onDone(p);
  };

  // Alt satırın düğmeleri ADIMIN tamamına ait — sekmeye göre DEĞİŞMEZ.
  const altSatir = {
    onSkip,
    skipTitle: "Yer bilgisi vermeden devam et",
    gonderAktif: !hicbiriYok,
    onGonder: gonder,
    gonderTitle: "Bu yer bilgisiyle devam et",
    pasif,
  };

  return (
    <div className="msj-yer">
      <div className="msj-secim-cipler" role="tablist" aria-label="Yer bilgisi">
        {SEKMELER.map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={sekme === s.key}
            title={`${s.ad} gir`}
            className={`msj-due-cip msj-secim-cip ${sekme === s.key ? "is-acik" : ""} ${
              doluMu[s.key] ? "is-dolu" : ""
            } ${parlaMi(s.key) ? "is-parla" : ""}`}
            disabled={pasif}
            onClick={() => setSekme(s.key)}
          >
            {s.ad}
            {s.key === "mahalle" && mahalleler.length > 0 && (
              <span className="msj-secim-sayi">{mahalleler.length}</span>
            )}
            {doluMu[s.key] && s.key !== "mahalle" && <CheckIcon className="msj-secim-tik" />}
          </button>
        ))}
        <span className="msj-secim-ipucu">üçü de isteğe bağlı</span>
      </div>

      {/* Her sekme KENDİ aracını + ortak alt satırı çizer; alt satır her
          durumda aynı yerde ve aynı düğmelerle durur. */}
      {sekme === "mahalle" && (
        <MahallePicker gomulu initial={mahalleler} onDegis={setMahalleler} altSatir={altSatir} />
      )}
      {sekme === "konum" && (
        <KonumSecici gomulu initial={nokta} onDegis={setNokta} altSatir={altSatir} />
      )}
      {sekme === "adres" && (
        <SihirbazAltSatir {...altSatir} sinif="msj-adres-kutu">
          <AdresIcerik deger={adres} onDegis={setAdres} />
        </SihirbazAltSatir>
      )}
    </div>
  );
}
